import React, { useEffect, useMemo, useRef, useState } from 'react';

import { KycMediaItem, fetchKycMedia, fetchKycMediaBlob, kycErrorMessage } from 'src/api/adminKyc';

import { KycImage, KycImageSlider } from './LegacyDocuments';
import { DocPlaceholder, FieldLabel, ImagesGrid, MediaNotice } from './styled';

const CONTEXT_LABEL: Record<string, string> = {
  'document-front': 'Document front',
  'document-back': 'Document back',
  face: 'Selfie',
  'document-and-face': 'Document with face',
};

const labelFor = (item: KycMediaItem) => {
  const key = String(item.context || item.name || '').toLowerCase();
  if (CONTEXT_LABEL[key]) return CONTEXT_LABEL[key];
  const text = key.replace(/[-_]/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Media';
};

// Document sides first, then the selfie, then anything else — the order a
// reviewer reads them in.
const ORDER = ['document-front', 'document-back', 'face', 'document-and-face'];
const rank = (item: KycMediaItem) => {
  const idx = ORDER.indexOf(String(item.context || '').toLowerCase());
  return idx === -1 ? ORDER.length : idx;
};

type Loaded = { item: KycMediaItem; url: string };

/**
 * Document and selfie images for a Veriff record, proxied through the API on
 * demand (videos are not shown — decision 24 Sep). Each file is fetched with the admin's bearer token into an object
 * URL; the URLs are revoked when the record closes, so nothing outlives the
 * modal in the browser either.
 */
const VeriffMedia: React.FC<{ kycId: string }> = ({ kycId }) => {
  const [items, setItems] = useState<KycMediaItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<Record<string, string>>({});
  const [sliderIndex, setSliderIndex] = useState<number | null>(null);
  const urlsRef = useRef<string[]>([]);

  useEffect(() => {
    let active = true;
    setItems(null);
    setError(null);
    setLoaded({});

    fetchKycMedia(kycId)
      .then((media) => {
        if (!active) return;
        const sorted = media.filter((item) => item.kind === 'image').sort((a, b) => rank(a) - rank(b));
        setItems(sorted);

        // Images only: the reviewer needs the document sides and the selfie;
        // the face video is Veriff's own liveness input and is not shown.
        sorted.filter((item) => item.kind === 'image').forEach((item) => {
          fetchKycMediaBlob(kycId, item.id)
            .then((url) => {
              if (!active) {
                URL.revokeObjectURL(url);
                return;
              }
              urlsRef.current.push(url);
              setLoaded((prev) => ({ ...prev, [item.id]: url }));
            })
            .catch(() => {
              if (active) setLoaded((prev) => ({ ...prev, [item.id]: '' }));
            });
        });
      })
      .catch((e) => {
        if (active) setError(kycErrorMessage(e, 'Could not load media from Veriff'));
      });

    return () => {
      active = false;
      urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      urlsRef.current = [];
    };
  }, [kycId]);

  const images = useMemo<Loaded[]>(
    () =>
      (items ?? [])
        .filter((item) => item.kind === 'image' && loaded[item.id])
        .map((item) => ({ item, url: loaded[item.id] })),
    [items, loaded],
  );
  const sliderImages = useMemo(() => images.map((entry) => entry.url), [images]);

  if (error) return <MediaNotice>{error}</MediaNotice>;
  if (items === null) return <DocPlaceholder>Loading media from Veriff…</DocPlaceholder>;
  if (items.length === 0) {
    return (
      <MediaNotice>
        Veriff holds no media for this session yet — the capture was not submitted, or the
        retention window has passed.
      </MediaNotice>
    );
  }

  const imageItems = items;

  return (
    <>
      <ImagesGrid>
        {imageItems.map((item) => {
          const url = loaded[item.id];
          return (
            <div key={item.id}>
              <FieldLabel>{labelFor(item)}</FieldLabel>
              {url === undefined ? (
                <DocPlaceholder>Loading…</DocPlaceholder>
              ) : url === '' ? (
                <DocPlaceholder>Could not load image</DocPlaceholder>
              ) : (
                <KycImage
                  src={url}
                  alt={labelFor(item)}
                  tall={String(item.context || '').toLowerCase() === 'face'}
                  onOpen={() => setSliderIndex(Math.max(0, sliderImages.indexOf(url)))}
                />
              )}
            </div>
          );
        })}
      </ImagesGrid>

      <div style={{ marginTop: '0.75rem' }}>
        <MediaNotice>
          Shown live from Veriff for this review. The API keeps no copy of these files.
        </MediaNotice>
      </div>

      {sliderIndex != null && sliderImages.length > 0 && (
        <KycImageSlider
          images={sliderImages}
          index={sliderIndex}
          onIndexChange={setSliderIndex}
          onClose={() => setSliderIndex(null)}
        />
      )}
    </>
  );
};

export default VeriffMedia;
